import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

import type {
  AttendeeInput,
  Order,
  OrderStatus,
  PaymentChoice,
  PublicTicketType,
  Quote,
} from './types'

export function usePublicTicketTypes(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['ticket-types', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicTicketType[]>(`/public/events/${slug}/ticket-types`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
  })
}

export interface Selection {
  ticket_type_id: string
  quantity: number
}

export interface FoodSelection {
  kind: 'item' | 'pack'
  ref_id: string
  quantity: number
}

export interface DrinkTablePayload {
  table_type_id: string
  party_size: number
  package_id: string | null
  items: { drink_item_id: string; quantity: number }[]
}

/**
 * Devis serveur.
 *
 * Le panier n'est jamais valorisé côté navigateur : à chaque modification, on
 * redemande au serveur ce que cela coûte. C'est lui qui connaît les prix, les
 * stocks et les fenêtres de vente.
 */
export function useQuote(eventId: string | undefined) {
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: (payload: {
      tickets: Selection[]
      food: FoodSelection[]
      /** Les bouteilles « sur commande », vendues a l'unite et hors table. */
      drinks?: { drink_item_id: string; quantity: number }[]
      drink_table?: DrinkTablePayload | null
      promo_code?: string | null
      /** « J'ai déjà mon billet » : vérifié dès le devis. */
      admission?: { order_number: string; email: string } | null
    }) =>
      apiClient.post<Quote>('/public/quotes', {
        event_id: eventId,
        locale: i18n.language,
        ...payload,
      }),
  })
}

export interface OrderPayload {
  quote_id: string
  quote_hash: string
  customer: {
    first_name: string
    last_name: string
    email: string
    /** Facultatif, au format international : le serveur refuse un numéro local. */
    whatsapp?: string
    locale: string
  }
  attendees: AttendeeInput[]
  consents: { terms: boolean; privacy: boolean; marketing: boolean }
  payment_choice?: PaymentChoice
  payment_plan?: 'single' | 'installments'
  /** Des plats sans billet ni table : la commande d'entrée du client. */
  admission_order_number?: string
}

/**
 * Création de commande.
 *
 * La clé d'idempotence vient de l'appelant, tirée **une fois par devis** :
 * un double-clic, ou un renvoi après une coupure réseau, repart avec la même
 * clé et le serveur rend la commande déjà créée au lieu d'en fabriquer une
 * seconde. Une clé neuve à chaque appel — ce qui se faisait ici — ne
 * protégeait de rien.
 */
export function useCreateOrder() {
  return useMutation({
    mutationFn: ({ idempotencyKey, ...payload }: OrderPayload & { idempotencyKey: string }) =>
      apiClient.post<Order>('/public/orders', payload, { idempotencyKey }),
  })
}

/** Les statuts d'où une commande ne bougera plus : inutile d'interroger encore. */
export const CLOSED_ORDER_STATUSES = new Set([
  'cancelled',
  'expired',
  'refunded',
  'partially_refunded',
])

/**
 * Suivi de commande.
 *
 * Le front ne décide jamais qu'un paiement a réussi : il interroge le serveur
 * jusqu'à ce que celui-ci l'ait constaté (Phase 10).
 */
export function useOrderStatus(orderId: string | undefined, token: string | null) {
  return useQuery({
    queryKey: ['order-status', orderId, token],
    queryFn: () =>
      apiClient.get<OrderStatus>(
        `/public/orders/${orderId}/status?token=${encodeURIComponent(token ?? '')}`,
      ),
    enabled: Boolean(orderId && token),
    // Payée, annulée, expirée ou remboursée : plus rien ne changera. Continuer
    // d'interroger toutes les quinze secondes, c'est tourner pour rien.
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 15_000
      return data.paid || CLOSED_ORDER_STATUSES.has(data.status) ? false : 15_000
    },
  })
}
